import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { ArrowRight, Plus } from "lucide-react";
import { useLets } from "@/hooks/useLets";
import { useAgent } from "@/hooks/useAgent";
import { can } from "@/lib/roles";
import { gbp, longDate } from "@/lib/format";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { SetupChecklist } from "@/components/agent/SetupChecklist";
import { PropertyImage } from "@/components/agent/PropertyImage";
import { ApplicantMark, DocStamps } from "@/components/agent/ApplicantMark";
import { DecisionTag, EmptyState, FraudTag, ListingTag, Loading, PageHeader, PrimaryButton, ProgressBar, SecondaryButton, VerdictTag } from "@/components/agent/ui";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

const ago = (iso) => (iso ? formatDistanceToNowStrict(new Date(iso), { addSuffix: true }) : "");

const Stat = ({ label, value, note, to, testid }) => (
  <Link to={to} className="flex flex-col gap-1 rounded-xl border bg-background p-4 transition-colors hover:bg-panel" data-testid={testid}>
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-2xl font-semibold tabular-nums tracking-tight">{value}</span>
    <span className="text-xs text-muted-foreground">{note}</span>
  </Link>
);

const Panel = ({ title, to, children, className, testid }) => (
  <section className={`flex min-w-0 flex-col gap-2.5 ${className || ""}`} data-testid={testid}>
    <div className="flex items-end justify-between gap-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {to && <Link to={to} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">View all <ArrowRight className="size-3" /></Link>}
    </div>
    <div className="flex flex-1 flex-col overflow-hidden rounded-xl border bg-background">{children}</div>
  </section>
);

const Quiet = ({ children }) => <p className="flex flex-1 items-center justify-center px-6 py-10 text-center text-sm text-muted-foreground">{children}</p>;

const ApplicantRow = ({ a, right, i, kind }) => (
  <Link to={`/lets/${a.let_.id}/applicants/${a.id}`} className="flex items-center gap-3 border-t px-4 py-3 transition-colors first:border-t-0 hover:bg-panel" data-testid={`overview-${kind}-${i}`}>
    <ApplicantMark a={a} />
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium">{a.label}</span>
      <span className="block truncate text-xs text-muted-foreground">{a.let_.address}</span>
    </span>
    {right}
  </Link>
);

const PropertyRow = ({ l, i }) => {
  const total = l.applicants.length;
  const done = l.applicants.filter((a) => a.status === "documents_complete").length;
  return (
    <Link to={`/lets/${l.id}/edit`} className="flex items-center gap-4 border-t px-4 py-3 transition-colors first:border-t-0 hover:bg-panel" data-testid={`overview-property-${i}`}>
      <span className="h-10 w-14 shrink-0 overflow-hidden rounded-md"><PropertyImage src={l.image_data} /></span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2"><span className="truncate text-sm font-medium">{l.address}</span><ListingTag status={l.listing_status} /></span>
        <span className="block text-xs text-muted-foreground">{gbp(l.monthly_rent)} a month · move in {longDate(l.move_in_date)}</span>
      </span>
      <span className="hidden w-48 shrink-0 sm:block">
        <span className="mb-1.5 flex items-center justify-between text-xs">
          <span className="font-medium">{total} {total === 1 ? "applicant" : "applicants"}</span>
          <span className="tabular-nums text-muted-foreground">{done} checked</span>
        </span>
        <ProgressBar value={done} total={total} />
      </span>
    </Link>
  );
};

export default function Overview() {
  const { lets, error, load } = useLets();
  const agent = useAgent();
  const [created, setCreated] = useState(null);
  const setup = lets && (lets.length === 0 || created);

  const data = useMemo(() => {
    const all = (lets || []).flatMap((l) => l.applicants.map((a) => ({ ...a, let_: l })));
    const checked = all.filter((a) => a.status === "documents_complete");
    const when = (a) => a.submitted_at || a.updated_at || "";
    return {
      all,
      checked: [...checked].sort((x, y) => when(y).localeCompare(when(x))),
      applying: all.filter((a) => a.status === "in_progress").sort((x, y) => (y.updated_at || "").localeCompare(x.updated_at || "")),
      waiting: checked.filter((a) => !a.decision && a.let_.listing_status !== "rented"),
      fraud: all.filter((a) => a.fraud_flag),
      available: (lets || []).filter((l) => l.listing_status !== "rented"),
    };
  }, [lets]);

  const first = (agent?.name || "").split(" ")[0];
  const addButton = can(agent?.role, "edit") && <PrimaryButton to="/lets/new" testid="overview-add-property"><Plus /> Add a property</PrimaryButton>;

  let body;
  if (error) body = <EmptyState title="Couldn't load your overview" text={error} action={<SecondaryButton onClick={load} testid="overview-retry">Try again</SecondaryButton>} testid="overview-error" />;
  else if (!lets) body = <Loading testid="overview-loading" />;
  else if (setup) body = <SetupChecklist created={created} onCreated={setCreated} onFinish={() => { setCreated(null); load(); }} />;
  else
    body = (
      <>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="overview-stats">
          <Stat label="Applicants" value={data.all.length} note={`${data.applying.length} still applying`} to="/applicants" testid="overview-stat-applicants" />
          <Stat label="Waiting for your decision" value={data.waiting.length} note="Checked, not yet answered" to="/applicants" testid="overview-stat-waiting" />
          <Stat label="Fraud flags" value={data.fraud.length} note={data.fraud.length ? "Open them before deciding" : "Nothing suspicious"} to="/applicants" testid="overview-stat-fraud" />
          <Stat label="Available properties" value={`${data.available.length} of ${lets.length}`} note={`${lets.length - data.available.length} rented`} to="/properties" testid="overview-stat-properties" />
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          <Panel title="Recent checks" to="/applicants" className="lg:col-span-3" testid="overview-recent">
            {data.checked.length ? data.checked.slice(0, 5).map((a, i) => (
              <ApplicantRow key={a.id} a={a} i={i} kind="recent" right={
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <span className="flex items-center gap-1.5">
                    <DecisionTag decision={a.decision} />
                    {a.fraud_flag ? <FraudTag /> : <VerdictTag status={a.status} outcome={a.outcome} />}
                  </span>
                  <span className="text-xs text-muted-foreground">{ago(a.submitted_at || a.updated_at)}</span>
                </span>
              } />
            )) : <Quiet>Finished checks appear here as soon as an applicant sends their documents.</Quiet>}
          </Panel>

          <Panel title="Still applying" to="/applicants" className="lg:col-span-2" testid="overview-applying">
            {data.applying.length ? data.applying.slice(0, 5).map((a, i) => (
              <ApplicantRow key={a.id} a={a} i={i} kind="applying" right={a.items?.length ? <DocStamps items={a.items} /> : null} />
            )) : <Quiet>No one is part-way through an application.</Quiet>}
          </Panel>
        </div>

        <Panel title="Properties" to="/properties" testid="overview-properties">
          {lets.slice(0, 6).map((l, i) => <PropertyRow key={l.id} l={l} i={i} />)}
        </Panel>
      </>
    );

  return (
    <AgentLayout wide>
      <PageHeader title={setup ? "Set up Landin" : first ? `${greeting()}, ${first}` : "Overview"}
        description={setup ? "Three steps and your first property is taking applications." : "Your applicants, their checks and your properties at a glance."}
        action={!setup && lets && addButton} />
      {body}
    </AgentLayout>
  );
}
